<template>
  <div class="wrapper-content">
    <LmComponentLoading v-if="isLoading" fixed />
    <div class="text-sm font-medium text-center text-gray-500 border-b border-gray-200 dark:text-gray-400 dark:border-gray-700">
      <ul class="flex">
        <li
          v-for="(tab, index) in tabs"
          :key="index"
          class="mr-1">
          <a
            @click="selectTab(index)"
            :class="[
              'inline-block py-4 px-6',
              currentTab === index ? 'text-blue-600 border-b-2 border-blue-600 rounded-t-lg active dark:text-blue-500 dark:border-blue-500' : 'border-b-2 border-transparent rounded-t-lg hover:text-gray-600 hover:border-gray-300 dark:hover:text-gray-300'
            ]"
          >
            {{ tab.name }}
          </a>
        </li>
      </ul>
    </div>
    <div class=" max-w-8xl mx-auto rounded-lg h-full mt-3">
      <div class="px-0 py-2 px-sm-4 py-sm-2">
        <div v-if="currentTab === 0">
          <h2 class="text-xl font-bold mb-4">Plan Actual</h2>
          <p v-if="isOnTrial">
            Estás en el periodo de prueba gratuita del plan <strong>{{ customerPlan.name }}.</strong>
            Te quedan <span class="font-bold">{{ daysLeft }}</span> día{{ daysLeft === 1 ? '' : 's' }}.
          </p>
          <p v-else>Estás suscrito al plan <strong>{{ customerPlan.name }}</strong>.</p>
          <p>Descripción del plan: {{ customerPlan.description }}</p>

          <!-- Indicadores gráficos -->
          <div class="mt-6">
            <h3 class="text-lg font-semibold mb-2">Indicadores de Consumo</h3>

            <!-- Indicador de usuarios creados -->
            <div class="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <lm-e-chart-pie-simple :title="'Usuarios'" :series="userChartData" :colors="userChartColors" />
              </div>

              <div>
                <lm-e-chart-pie-simple :title="'Documentos por Mes'" :series="documentChartData" :colors="documentChartColors"/>
              </div>
            </div>
          </div>
        </div>
        <div v-if="currentTab === 1">
          <LmPricingTable :plans="plans" @reload="loadData"/>
        </div>
        <div v-if="currentTab === 2">
          <LmDataTable
            slug="invoices-todas"
            hideButtons
          />
        </div>

        <div v-if="currentTab === 3">
          <h3>A nombre de quien debemos emitir la factura</h3>
          <b-row>
            <LmTextInput
              class="col-12 col-md-4"
              label="Razón Social o Nombre"
              :v="v('settings.billingInformation.name')"
              v-model="$v.settings.billingInformation.name.$model"
            />
            <div class="col-12 col-md-4">
              <label class="pb-2" for="cuit">CUIT/CUIL</label>
              <b-form-input
                v-model="$v.settings.billingInformation.identificator.$model"
                :v="v('settings.billingInformation.identificator')"
                placeholder="00-00000000-0"
                v-mask="'##-########-#'"
              />
              <p v-if="$v.settings.billingInformation.identificator.$error">
                <span v-if="!$v.settings.billingInformation.identificator.required">Es obligatorio</span>
                <span v-if="!$v.settings.billingInformation.identificator.isValidCUITCUIL">CUIT o CUIL inválido</span>
              </p>
            </div>
            <LmEmailInput
              class="col-12 col-md-4"
              :v="v('settings.billingInformation.email')"
              v-model="$v.settings.billingInformation.email.$model"
              label="Email"
            />
            <LmTextInput
              class="col-12 col-md-4"
              label="Dirección"
              v-model="settings.billingInformation.address"
            />
            <LmTextInput
              class="col-12 col-md-4"
              label="Teléfono"
              v-model="settings.billingInformation.phone"
            />
          </b-row>
        </div>

        <div v-if="currentTab === 4">
          <b-card no-body class="border">
            <b-tabs card>
              <b-tab :title="$t('common')" active class="py-4">
                <h3>{{$t('personalization')}}</h3>
                <b-row>
                  <div class="col-12 col-md-8 overlay-image">
                    <span class="d-block mb-2">{{$t('header_logo')}}</span>
                    <div class="img-wrap mt-2" v-if="appLogo1">
                      <b-img class="img-preview" center thumbnail :src="appLogo1"></b-img>
                      <div class="img-description-layer">
                        <p v-b-modal.logo-1-cropper-modal class="img-description">{{$t('change_logo')}}</p>
                        <font-awesome-icon class="ml-4 clickable img-description" icon="trash" @click="onRemoveLogo()" style="width:16px; height:16px;" />
                      </div>
                    </div>
                    <div
                      v-else
                      class="empty-logo d-flex justify-content-center align-items-center">
                      <p v-b-modal.logo-1-cropper-modal class="mb-0">{{$t('click_to_change_logo')}}</p>
                    </div>
                  </div>
                  <div class="col-12 col-md-4">
                    <LmColorPicker
                      label="Menu: Color de Fondo"
                      v-model="settings.coreSettings.menuBgColor"
                      :showFallback="true"
                      shapes="squares"
                    />
                  </div>
                </b-row>
              </b-tab>
              <b-tab title="Documentos"  class="py-4">
                <h3>Opciones de PDF</h3>
                <b-row>
                  <LmTextInput
                    class="col-12 col-md-4"
                    label="Título del encabezado"
                    placeholder="Título del encabezado"
                    v-model="settings.documents.pdfHeader"
                  />
                  <LmTextInput
                    class="col-12 col-md-4"
                    label="Subtítulo del encabezado"
                    placeholder="Subtítulo del encabezado"
                    v-model="settings.documents.pdfSubHeader"
                  />
                  <div class="col-12 col-md-4 overlay-image">
                    <span class="d-block">Logo del encabezado</span>
                    <div class="img-wrap" v-if="pdfLogoPreviewSrc">
                      <b-img class="img-preview" center thumbnail :src="pdfLogoPreviewSrc"></b-img>
                      <div class="img-description-layer">
                        <p v-b-modal.logo-pdf-cropper-modal class="img-description">Editar logo</p>
                        <font-awesome-icon class="ml-4 clickable img-description" icon="trash" @click="onRemovePdfLogo()" style="width:16px; height:16px;" />
                      </div>
                    </div>
                    <div
                      v-else
                      class="empty-logo d-flex justify-content-center align-items-center">
                      <p v-b-modal.logo-pdf-cropper-modal class="mb-0">Carga un logo desde acá</p>
                    </div>
                  </div>

                </b-row>
              </b-tab>
              <b-tab title="Templates de Email" class="py-4">
                <h3>Documentos</h3>
                <b-row>
                  <LmTreeSelect
                    class="col-12 col-md-4"
                    label="Notificación de documento a firmar o revisar"
                    :options="mailertemplateOptions"
                    v-model="settings.mailerTemplates.signersEmailTemplate"
                  />
                  <LmTreeSelect
                    class="col-12 col-md-4"
                    label="Documento Finalizado"
                    :options="mailertemplateOptions"
                    v-model="settings.mailerTemplates.documentFinishedEmailTemplate"
                  />
                  <LmTreeSelect
                    class="col-12 col-md-4"
                    label="Notificación para participante externo"
                    :options="mailertemplateOptions"
                    v-model="settings.mailerTemplates.externalEmailTemplate"
                  />
                </b-row>
                <hr class="w-100">
                <h3>Trámites</h3>
                <b-row>
                  <LmTreeSelect
                    class="col-12 col-md-4"
                    label="Notificaciones"
                    :options="mailertemplateOptions"
                    v-model="settings.mailerTemplates.processNotification"
                  />
                  <LmTreeSelect
                    class="col-12 col-md-4"
                    label="Trámite listo para descargar"
                    :options="mailertemplateOptions"
                    v-model="settings.mailerTemplates.processZipReady"
                  />
                </b-row>
              </b-tab>
            </b-tabs>
          </b-card>
        </div>
      </div>
    </div>

    <b-modal id="logo-pdf-cropper-modal" no-close-on-backdrop :title="$t('image_editor')" size="xl">
      <div id="image-for-avatar-wrapper" class="row">
        <div class="col-12">
          <LmProfileCropper
            ref="lmCropperAppLogoPdf"
            hideSaveButton
            hideCropButton
            hideCropImg
            imgToCropWidthAsContainer
            :viewMode="1"
            labelFile="Logo del encabezado"
            @input="settings.documents.pdfLogo = $event; closeCropper('logo-pdf-cropper-modal', 'lmCropperAppLogoPdf');"
            @imageLoadedToBeCrop="isImageLoadedToBeCrop = true"
            @reset="isImageLoadedToBeCrop = false"
            :imgSrc="imageToCrop"
            />
        </div>
      </div>
      <template v-slot:modal-footer>
        <div class="w-100">
          <LmButton
            :disabled="!isImageLoadedToBeCrop"
            id="btn-crop-save-avatar"
            type="button"
            lmButtonRootWrapperClass="d-inline-block mr-1 mt-3"
            buttonClass="btn-primary"
            value="Guardar"
            class="float-right"
            @click="cropImage('lmCropperAppLogoPdf')"
          />
          <LmButton
            id="btn-crop-cancel-avatar"
            type="button"
            lmButtonRootWrapperClass="d-inline-block mr-1 mt-3"
            buttonClass="btn-secondary"
            :value="$t('Cancel')"
            class="float-right"
            @click="closeCropper('logo-pdf-cropper-modal', 'lmCropperAppLogoPdf')"
          />
        </div>
      </template>
    </b-modal>

    <b-modal id="logo-1-cropper-modal" no-close-on-backdrop :title="$t('image_editor')" size="xl">
      <div id="image-for-avatar-wrapper" class="row">
        <div class="col-12">
          <LmProfileCropper
            ref="lmCropperAppLogo1"
            hideSaveButton
            hideCropButton
            hideCropImg
            imgToCropWidthAsContainer
            :viewMode="1"
            labelFile="Logo del encabezado"
            @input="settings.customization.appLogo1 = $event; closeCropper('logo-1-cropper-modal', 'lmCropperAppLogo1');"
            @imageLoadedToBeCrop="isImageLoadedToBeCrop = true"
            @reset="isImageLoadedToBeCrop = false"
            :imgSrc="imageToCrop"
            />
        </div>
      </div>
      <template v-slot:modal-footer>
        <div class="w-100">
          <LmButton
            :disabled="!isImageLoadedToBeCrop"
            id="btn-crop-save-avatar"
            type="button"
            lmButtonRootWrapperClass="d-inline-block mr-1 mt-3"
            buttonClass="btn-primary"
            value="Guardar"
            class="float-right"
            @click="cropImage('lmCropperAppLogo1')"
          />
          <LmButton
            id="btn-crop-cancel-avatar"
            type="button"
            lmButtonRootWrapperClass="d-inline-block mr-1 mt-3"
            buttonClass="btn-secondary"
            :value="$t('Cancel')"
            class="float-right"
            @click="closeCropper('logo-1-cropper-modal', 'lmCropperAppLogo1')"
          />
        </div>
      </template>
    </b-modal>
    <div class="buttons" v-if="currentTab === 3 || currentTab === 4">
      <LmButton
        :value="$t('save')"
        buttonClass="btn-primary"
        @click="onSaveBillingInformation"
        v-if="currentTab === 3"
      />
      <LmButton
        :value="$t('save')"
        buttonClass="btn-primary"
        @click="onSave"
        v-if="currentTab === 4"
      />
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import LmProfileCropper from '@/components/LmProfileCropper.vue'
import LmTreeSelect from '@/components/form/elements/LmTreeSelect'
import LmTextInput from '@/components/form/elements/LmTextInput'
import LmEmailInput from '@/components/form/elements/LmEmailInput'
import LmColorPicker from '@/components/form/elements/LmColorPicker'
import LmUploadFiles from '@/components/form/elements/LmUploadFiles'
import LmButton from '@/components/form/elements/LmButton'
import LmDataTable from '@/components/lmDataTable/table'

import LmEChartPieSimple from '@/components/charts/LmEChartPieSimple'
import LmPricingTable from '@/components/LmPricingTable'
import _ from 'lodash'

import commonsApi from '@/api/commons'
import lmToasts from '@/mixins/lmToasts.js'

import { required, email } from 'vuelidate/lib/validators'
import isValidCUITCUIL from '@/helpers/isValidCUITCUIL'

export default {
  mixins: [lmToasts],
  components: {
    LmTreeSelect,
    LmTextInput,
    LmEmailInput,
    LmColorPicker,
    LmUploadFiles,
    LmButton,
    LmProfileCropper,
    LmEChartPieSimple,
    LmDataTable,
    LmPricingTable
  },
  data () {
    return {
      currentTab: 0,
      tabs: [
        { name: 'Plan Actual' },
        { name: 'Planes Disponibles' },
        { name: 'Facturas' },
        { name: 'Información de Facturación' },
        { name: 'Ajustes' }
      ],
      customerPlan: {
        id: 0,
        name: '',
        description: ''
      },
      createdUsers: 0, // Número de usuarios creados
      createdDocuments: 0, // Número de documentos creados en el mes
      userLimit: 0, // Límite de usuarios para el plan
      documentLimit: 0, // Límite de documentos para el plan
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false
      },
      plans: [],
      isLoading: false,
      isImageLoadedToBeCrop: false,
      imageToCrop: '',
      mailertemplateOptions: [],
      settings: {
        billingInformation: {
          name: '',
          identificator: '',
          address: '',
          email: '',
          phone: ''
        },
        documents: {
          pdfHeader: null,
          pdfSubHeader: null,
          pdfLogo: null
        },
        mailerTemplates: {
          signersEmailTemplate: null,
          documentFinishedEmailTemplate: null,
          externalEmailTemplate: null,
          processZipReady: null,
          processNotification: null
        },
        customization: {
          appLogo1: null
        },
        coreSettings: {
          menuBgColor: '#004181'
        }
      }
    }
  },
  validations: {
    settings: {
      billingInformation: {
        name: {
          required
        },
        email: {
          required,
          email
        },
        identificator: {
          required,
          isValidCUITCUIL: isValidCUITCUIL()
        }
      }
    }
  },
  computed: {
    ...mapGetters('session', ['accountUserName', 'emailWasConfirmed', 'isOnTrial', 'daysLeft']),
    userChartColors () {
      return ['#42A5F5', '#BDBDBD']
    },
    documentChartColors () {
      return ['#66BB6A', '#BDBDBD']
    },
    userChartData () {
      const remaining = this.userLimit - this.createdUsers
      return [
        { value: this.createdUsers, name: `Creados ${this.createdUsers}` },
        { value: remaining, name: `Restantes ${remaining}` }
      ]
    },
    documentChartData () {
      const remaining = this.documentLimit - this.createdDocuments
      return [
        { value: this.createdDocuments, name: `Creados ${this.createdDocuments}` },
        { value: remaining, name: `Restantes ${remaining}` }
      ]
    },
    userProgress () {
      return (this.createdUsers / this.userLimit) * 100
    },
    documentProgress () {
      return (this.createdDocuments / this.documentLimit) * 100
    },
    mailerTemplatePath () {
      return '/mailertemplate/all?type=email&format=minimal'
    },
    pdfLogoPreviewSrc () {
      if (this.settings?.documents?.pdfLogo?.path) {
        return process.env.VUE_APP_API_URL_STATIC_ASSETS + this.settings.documents.pdfLogo.path
      }
      return null
    },
    appLogo1 () {
      if (this.settings?.customization?.appLogo1?.path) {
        return process.env.VUE_APP_API_URL_STATIC_ASSETS + this.settings.customization.appLogo1.path
      }
      return null
    }
  },
  methods: {
    ...mapActions('settings', ['getCoreSettings', 'getCustomerSettings']),
    validateForm (validation) {
      validation.$touch()
      if (validation.$error) {
        this.lmToastDangerMessage(this.$t('form_with_errors'), 'Error')
        throw new Error('validation error')
      } else {
        if (validation.$pending || validation.$error) {
          throw new Error('validation error')
        }
      }
    },
    v (position) {
      return _.get(_.cloneDeep(this.$v), position)
    },
    selectTab (index) {
      this.currentTab = index
      this.$router.push({ query: { tab: index } })
    },
    async onSaveBillingInformation () {
      try {
        await this.validateForm(this.$v)
        this.isLoading = true
        await commonsApi.axiosPutWrapper('/customersettings/update_billing', { billingInformation: this.settings.billingInformation })
        await this.getCustomerSettings()
        this.lmToastSuccessMessage('Se guardaron los cambios.', 'Se guardó exitosamente')
      } catch (error) {
        console.log(error)
      } finally {
        this.isLoading = false
      }
    },
    async onSave () {
      try {
        this.isLoading = true
        await commonsApi.axiosPutWrapper('/customersettings/update', this.settings)
        await this.getCoreSettings()
        await this.getCustomerSettings()
        this.lmToastSuccessMessage('Se guardaron los cambios.', 'Se guardó exitosamente')
      } catch (error) {
        console.log(error)
      } finally {
        this.isLoading = false
      }
    },

    cropImage (ref) {
      this.$refs[ref].crop()
      this.$refs[ref].submit()
    },

    closeCropper (id, ref) {
      this.$forceUpdate()
      this.$bvModal.hide(id)
      this.imageToCrop = ''
      this.$refs[ref].reset()
    },

    onRemoveLogo () {
      this.settings.customization.appLogo1 = null
    },

    onRemovePdfLogo () {
      this.settings.documents.pdfLogo.path = null
    },

    async loadData () {
      const mailertemplateResponse = await commonsApi.axiosGetWrapper(this.mailerTemplatePath)
      this.mailertemplateOptions = mailertemplateResponse.data.items.map(el => ({ id: el._id, name: el.name }))

      const settings = await commonsApi.axiosGetWrapper('/customersettings/mysettings/documents,mailerTemplates,customization,billingInformation')

      for (const key in settings.data) {
        for (const innerKey in settings.data[key]) {
          this.settings[key][innerKey] = settings.data[key][innerKey]
        }
      }

      const plans = await commonsApi.axiosGetWrapper('/customer/plans/details')
      this.plans = plans.data.plans
      this.createdUsers = plans.data.createdUsers
      this.customerPlan = plans.data.customerPlan
      this.createdDocuments = plans.data.createdDocuments
      this.userLimit = plans.data.userLimit
      this.documentLimit = plans.data.documentLimit

      // En caso de existir menuBgColor en customization, lo usamos ya que ese debería
      // haber sido el lugar correcto desde donde tomar este atributo.
      if (this.settings?.customization?.menuBgColor) {
        this.settings.coreSettings.menuBgColor = this.settings.customization.menuBgColor
      }
    }
  },
  async created () {
    const tabIndex = parseInt(this.$route.query.tab) || 0
    if (tabIndex >= 0 && tabIndex < this.tabs.length) {
      this.currentTab = tabIndex
    }
    try {
      this.isLoading = true
      this.loadData()
    } catch (error) {
      console.log(error)
    } finally {
      this.isLoading = false
    }
  }
}
</script>
<style lang="sass">
.overlay-image
  .empty-logo
    margin-top: 14px
    height: 38px
    background-color: #eaeaea
    border-radius: 5px
    border: 2px dashed #d1d0d0
    p:focus-visible
      outline: none
  .img-wrap
    margin-top: 12px
    position: relative
    cursor: pointer
  .img-preview
    max-width: auto
    height: 100px
  .img-description-layer
    position: absolute
    top: 0
    bottom: 0
    left: 0
    right: 0
    border-radius: 5px
    background: var(--app-accent-color)
    color: #fff
    visibility: hidden
    opacity: 0
    display: flex
    align-items: center
    justify-content: center
    transition: opacity .2s, visibility .2s
  .img-wrap:hover .img-description-layer
    visibility: visible
    opacity: 1
  .img-description
    margin-bottom: 0
    transition: .2s
    transform: translateY(1em)
    text-transform: uppercase
  .img-wrap:hover .img-description
    transform: translateY(0)
</style>
